import type { WorkspaceTable } from '../../../services/workspaceApi';
import type { MaterializationResult } from '../hooks/useMappingWorkflow';

const MAPPED_VIEW_PATTERN = /^__mapped_.+__$/;

export function isMappedWorkspaceViewName(name: string): boolean {
  return MAPPED_VIEW_PATTERN.test(name);
}

/** DuckDB view name for a canonical target table (matches backend materializer). */
export function viewNameForTargetTable(tableName: string): string {
  const sanitized = tableName.toLowerCase().replace(/[^a-z0-9_]/g, '_');
  return `__mapped_${sanitized}__`;
}

export function targetTableFromMappedView(viewName: string): string {
  if (!isMappedWorkspaceViewName(viewName)) return viewName;
  return viewName.slice('__mapped_'.length, -2);
}

export function listMappedWorkspaceViews(workspaceTables: WorkspaceTable[]): WorkspaceTable[] {
  return workspaceTables.filter((t) => isMappedWorkspaceViewName(t.name));
}

/**
 * Rebuild in-memory materialization metadata from live workspace tables.
 * Used after navigation/remount when DuckDB still has views but React state was lost.
 */
export function materializationResultFromWorkspaceTables(
  workspaceTables: WorkspaceTable[],
  targetTables: string[] = [],
): MaterializationResult | null {
  const mappedTables = listMappedWorkspaceViews(workspaceTables);
  if (mappedTables.length === 0) return null;

  const canonicalByView = new Map<string, string>();
  for (const tableName of targetTables) {
    canonicalByView.set(viewNameForTargetTable(tableName), tableName);
  }

  return {
    viewDetails: mappedTables.map((t) => ({
      view_name: t.name,
      target_table: canonicalByView.get(t.name) ?? targetTableFromMappedView(t.name),
      ddl: '',
      column_count: t.column_count ?? 0,
    })),
    failedViews: [],
  };
}

export function mappedOutputsFromWorkspaceTables(
  workspaceTables: WorkspaceTable[],
  targetTables: string[] = [],
): Array<{
  outputRef: string;
  displayName: string;
  rowCount?: number;
  columnCount?: number;
}> {
  const canonicalByView = new Map<string, string>();
  for (const tableName of targetTables) {
    canonicalByView.set(viewNameForTargetTable(tableName), tableName);
  }

  return listMappedWorkspaceViews(workspaceTables).map((t) => ({
    outputRef: t.name,
    displayName: canonicalByView.get(t.name) ?? targetTableFromMappedView(t.name),
    rowCount: t.row_count,
    columnCount: t.column_count,
  }));
}
